import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import avulsaService from '../services/avulsaService';
import API_URL from '../config';
import Button from './ui/Button';
import './NCForm.css';

/**
 * Análise Avulsa V1 — preenchimento da NC (a "isca" da entrada), SEM IA.
 *
 * Passo GRÁTIS do funil: o comprador monta a NC com calma e só paga depois.
 * Chama POST /api/avulsa/nc/fill (rascunho, modo Investigação fixo no backend).
 *
 * PARIDADE com a análise convencional (NCForm): todos os campos de
 * preenchimento — origem_tipo (próprio/cliente/fornecedor) com blocos
 * condicionais, produto, lote, data, como-detectada, quantidade, status do lote,
 * indústria, contexto + 6M, aplicação no cliente. O backend de fill aceita
 * todos (#287).
 *
 * ⭐ IMPORTADOR (a isca grande): "tem um documento do cliente? a gente preenche
 * pra você." POST /api/avulsa/nc/importar (extração por IA barata, PÓS-cadastro,
 * NÃO cria NC) → pré-preenche o formulário. Travas server-side: 3×/compra +
 * janela por IP + 10MB. Roda só depois do cadastro (CNPJ + consent já dados).
 */

// MIME aceitos pelo backend (SUPPORTED_MIME_TYPES) — espelhado no client p/
// feedback imediato. Limite 10MB (IMPORT_MAX_BYTES, menor que os 25MB do app).
const IMPORT_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};
const IMPORT_MAX_BYTES = 10 * 1024 * 1024;

// Opções do select "Como foi detectada" (espelham o NCForm). Se a extração
// trouxer algo fora dessa lista, cai no campo livre (sentinel '__outro__').
const ORIGEM_OPCOES = ['Inspeção', 'Cliente', 'Auditoria', 'Produção', 'Recebimento', 'Expedição', 'Laboratório'];
const OUTRO_SENTINEL = '__outro__';

// Rascunho persistido: o que foi preenchido/importado sobrevive a voltar do
// checkout (o componente remonta e zerava o state) E a recarregar a página.
// Limpo no fim da sessão avulsa (avulsaService.clearSession).
const DRAFT_KEY = 'avulsa_fill_draft';
function _loadFillDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}') || {}; }
  catch { return {}; }
}

// Setores de serviço: escondem Lote / Status do Lote (espelha NCForm).
const SERVICE_SECTOR_VALUES = new Set([
  'contabil', 'saude', 'logistica', 'facilities', 'laboratorios', 'ti',
  'engenharia_projetos', 'servicos',
  'contábil / fiscal', 'saúde / clínicas', 'logística / armazenagem (3pl)',
  'facilities / terceirizados', 'laboratórios (calibração/ensaios)', 'ti / software',
  'engenharia / projetos', 'serviços',
]);
const isServiceSector = (industria) =>
  industria != null && industria !== '' && SERVICE_SECTOR_VALUES.has(String(industria).toLowerCase().trim());

function AvulsaFillForm({ onFilled }) {
  const [form, setForm] = useState(() => ({
    descricao_nc: '',
    contexto_processo: '',
    contexto_setorial_custom: '',
    industria: '',
    produto: '',
    lote: '',
    data_ocorrencia: '',
    origem: '',
    quantidade_afetada: '',
    status_lote: '',
    origem_tipo: 'proprio',
    cliente_emissor: '',
    nome_cliente: '',
    numero_nc_cliente: '',
    prazo_resposta_cliente: '',
    contato_cliente_email: '',
    fornecedor_nome: '',
    ...(_loadFillDraft().form || {}),  // rehidrata rascunho (volta do checkout / reload)
  }));
  // Texto livre quando o usuário escolhe "Outro..." em origem/indústria.
  const [origemCustom, setOrigemCustom] = useState(() => _loadFillDraft().origemCustom || '');
  const [industriaCustom, setIndustriaCustom] = useState(() => _loadFillDraft().industriaCustom || '');
  const [industrias, setIndustrias] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Importador (a isca)
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  // { confianca, campos: [{campo,motivo}], arquivo, restantes }
  const [importInfo, setImportInfo] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Indústrias disponíveis (endpoint público — mesmo do NCForm).
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/v2/nc/industrias`);
        const data = await r.json();
        if (!cancelado) setIndustrias(data.industrias || []);
      } catch (e) { /* sem dropdown — usuário usa "Outro" */ }
    })();
    return () => { cancelado = true; };
  }, []);

  // Persiste o rascunho a cada mudança — o que foi preenchido/importado sobrevive
  // a voltar do checkout (componente remonta) E a recarregar a página. Some só no
  // fim da sessão avulsa (avulsaService.clearSession remove a chave).
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, origemCustom, industriaCustom }));
    } catch (e) { /* storage indisponível — segue sem persistir */ }
  }, [form, origemCustom, industriaCustom]);

  // ── Importador: mapeia dados_extraidos → estado do form ──────────────────────
  const aplicarExtraidos = useCallback((res) => {
    const d = res.dados_extraidos || {};
    const patch = {};
    if (d.descricao_nc) patch.descricao_nc = d.descricao_nc;
    if (d.produto) patch.produto = d.produto;
    if (d.lote) patch.lote = d.lote;
    if (d.data_ocorrencia) patch.data_ocorrencia = d.data_ocorrencia;
    if (d.quantidade_afetada) patch.quantidade_afetada = d.quantidade_afetada;
    if (d.numero_nc_cliente) patch.numero_nc_cliente = d.numero_nc_cliente;
    if (d.prazo_resposta_cliente) patch.prazo_resposta_cliente = d.prazo_resposta_cliente;
    if (d.contato_cliente_email) patch.contato_cliente_email = d.contato_cliente_email;
    // Documento do cliente → quase sempre é uma reclamação externa. Já mostra o bloco.
    if (d.cliente_emissor) {
      patch.cliente_emissor = d.cliente_emissor;
      patch.origem_tipo = 'cliente';
    }
    // "Como foi detectada": casa com a lista do select; senão, vira texto livre.
    if (d.origem) {
      const hit = ORIGEM_OPCOES.find((o) => o.toLowerCase() === String(d.origem).toLowerCase().trim());
      if (hit) { patch.origem = hit; }
      else { patch.origem = OUTRO_SENTINEL; setOrigemCustom(d.origem); }
    }
    // Indústria: casa com código/nome do dropdown; senão, texto livre.
    if (d.industria) {
      const e = String(d.industria).toLowerCase().trim();
      const hit = industrias.find((i) => String(i.codigo).toLowerCase() === e || String(i.nome).toLowerCase() === e);
      if (hit) { patch.industria = hit.codigo; }
      else { patch.industria = OUTRO_SENTINEL; setIndustriaCustom(d.industria); }
    }
    setForm((f) => ({ ...f, ...patch }));
  }, [industrias]);

  const handleImport = useCallback(async (file) => {
    if (!file) return;
    setImportError('');
    setImporting(true);
    try {
      const res = await avulsaService.importar(file);
      aplicarExtraidos(res);
      setImportInfo({
        confianca: res.confianca_extracao || 'baixa',
        campos: res.campos_com_duvida || [],
        arquivo: res.arquivo_original_nome || file.name,
        restantes: typeof res.extracoes_restantes === 'number' ? res.extracoes_restantes : null,
      });
      avulsaService.event('import_extracted', { confianca: res.confianca_extracao });
    } catch (err) {
      // Mensagens server-side já são amigáveis (429 cap/janela, 413, 400 tipo).
      setImportError(
        err.status === 401
          ? 'Sua sessão expirou. Recarregue a página e entre de novo.'
          : (err.message || 'Não consegui ler o documento agora. Tente outro arquivo ou preencha manualmente.')
      );
    } finally {
      setImporting(false);
    }
  }, [aplicarExtraidos]);

  const onDrop = useCallback((accepted, rejected) => {
    setImportError('');
    if (rejected && rejected.length > 0) {
      const code = rejected[0].errors?.[0]?.code;
      if (code === 'file-too-large') setImportError('Arquivo acima de 10MB. Comprima ou envie um menor.');
      else if (code === 'file-invalid-type') setImportError('Tipo não suportado. Aceitos: PDF, DOCX, PNG, JPG, WEBP.');
      else setImportError(rejected[0].errors?.[0]?.message || 'Arquivo inválido.');
      return;
    }
    if (accepted && accepted.length > 0) handleImport(accepted[0]);
  }, [handleImport]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: IMPORT_ACCEPT,
    multiple: false,
    maxSize: IMPORT_MAX_BYTES,
    disabled: importing,
    onDrop,
  });

  const importEsgotado = importInfo && importInfo.restantes === 0;
  const industriaResolvida = form.industria === OUTRO_SENTINEL ? industriaCustom : form.industria;
  const ehServico = isServiceSector(industriaResolvida);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.descricao_nc.trim()) {
      setError('Descreva a não-conformidade para continuar.');
      return;
    }
    if (form.origem_tipo === 'cliente' && !form.cliente_emissor.trim()) {
      setError('Informe o cliente que emitiu a NC (aparece no relatório).');
      return;
    }
    if (form.origem_tipo === 'fornecedor' && !form.fornecedor_nome.trim()) {
      setError('Informe o fornecedor envolvido.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      // Resolve os sentinels "Outro" pro texto livre.
      payload.origem = form.origem === OUTRO_SENTINEL ? origemCustom.trim() : form.origem;
      payload.industria = form.industria === OUTRO_SENTINEL ? industriaCustom.trim() : form.industria;
      // Filtra campos por origem_tipo (espelha o NCForm: não vaza campo de outro modo).
      if (payload.origem_tipo !== 'cliente') {
        payload.cliente_emissor = '';
        payload.numero_nc_cliente = '';
        payload.prazo_resposta_cliente = '';
        payload.contato_cliente_email = '';
      }
      if (payload.origem_tipo !== 'fornecedor') payload.fornecedor_nome = '';
      if (payload.origem_tipo !== 'proprio') payload.nome_cliente = '';
      const res = await avulsaService.fill(payload);
      onFilled(res); // { nc_id, numero_nc, ... } → orquestrador avança pro checkout
    } catch (err) {
      setError(
        err.status === 401
          ? 'Sua sessão expirou. Recarregue a página e entre de novo.'
          : (err.message || 'Não foi possível salvar. Tente novamente.')
      );
    } finally {
      setLoading(false);
    }
  };

  const hintStyle = { fontSize: 11, color: '#57657a', marginTop: 4, display: 'block' };

  return (
    <form onSubmit={handleSubmit} className="nc-form-container" style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* ⭐ Importador — a isca */}
      <div style={{
        marginBottom: 24, padding: '16px 18px', background: '#f0fdf4',
        border: '1px solid #bbf7d0', borderRadius: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className="material-symbols-outlined" style={{ color: '#10b981' }}>upload_file</span>
          <strong style={{ fontSize: 15, color: '#065f46' }}>Tem o documento do cliente? A gente preenche pra você</strong>
        </div>
        <p style={{ fontSize: 13, color: '#047857', margin: '0 0 10px', lineHeight: 1.5 }}>
          Recebeu uma RNC, um 8D ou um e-mail de reclamação? Envie o arquivo e a IA extrai os campos
          automaticamente — você só revisa. <strong>Opcional</strong>: dá pra preencher na mão também.
        </p>

        {!importEsgotado && (
          <div
            {...getRootProps()}
            className={`nc-dropzone${isDragActive ? ' active' : ''}`}
            style={{ background: '#fff', cursor: importing ? 'wait' : 'pointer', opacity: importing ? 0.7 : 1 }}
          >
            <input {...getInputProps()} />
            <div className="nc-dropzone-icon material-symbols-outlined">
              {importing ? 'hourglass_top' : 'attach_file'}
            </div>
            <p>
              {importing
                ? 'Lendo o documento…'
                : (isDragActive ? 'Solte o arquivo aqui…' : 'Arraste o documento aqui ou clique para selecionar')}
            </p>
            <small>PDF, DOCX, PNG, JPG ou WEBP — até 10MB</small>
          </div>
        )}

        {importError && (
          <div className="error-message" style={{ marginTop: 10 }}>{importError}</div>
        )}

        {importInfo && !importError && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 6,
            background: '#fff', border: '1px solid #bbf7d0', fontSize: 12.5, color: '#065f46',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
              <span>
                Dados extraídos de <strong>{importInfo.arquivo}</strong>
                {importInfo.confianca && <> · confiança <strong>{importInfo.confianca}</strong></>}.
                {' '}Revise os campos abaixo antes de continuar.
              </span>
            </div>
            {importInfo.campos && importInfo.campos.length > 0 && (
              <div style={{ marginTop: 6, color: '#92400e' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: 'text-bottom' }}>warning</span>{' '}
                Confira com atenção: {importInfo.campos.map((c) => c.campo).join(', ')}.
              </div>
            )}
            {importInfo.restantes != null && (
              <div style={{ marginTop: 6, color: '#57657a' }}>
                {importInfo.restantes > 0
                  ? `Importou o documento errado? Você ainda pode importar outro documento ${importInfo.restantes}×.`
                  : 'Limite de importações atingido — siga preenchendo na mão.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* O quê */}
      <div className="nc-form-group">
        <label className="nc-form-label">O que aconteceu? (a não-conformidade) *</label>
        <textarea
          className="nc-form-input" name="descricao_nc" rows="3" required
          value={form.descricao_nc} onChange={set('descricao_nc')}
          placeholder="Descreva o problema de forma objetiva. Ex: 38 de 500 conexões hidráulicas vazaram no teste de estanqueidade do cliente."
          disabled={loading}
        />
        <small style={hintStyle}>
          Escreva a não-conformidade do jeito que ela chegou até você — o problema em si, como foi relatado. Se o relato já trouxe quanto, onde e quando, inclua; o "como" e o "porquê" você detalha no campo de contexto, mais abaixo.
        </small>
      </div>

      {/* Origem da NC (quem está apontando) */}
      <div className="nc-form-group" style={{ marginBottom: 16, padding: '12px 16px', background: '#f8fafc', borderLeft: '3px solid #006948', borderRadius: 6 }}>
        <label className="nc-form-label" style={{ marginBottom: 8 }}>Origem da NC *</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="radio" name="origem_tipo" value="proprio"
              checked={form.origem_tipo === 'proprio'} onChange={set('origem_tipo')} disabled={loading} />
            <span><strong>Interna (própria)</strong> — detectada pela sua equipe</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="radio" name="origem_tipo" value="cliente"
              checked={form.origem_tipo === 'cliente'} onChange={set('origem_tipo')} disabled={loading} />
            <span><strong>Cliente externo</strong> — reclamação recebida de um cliente seu</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="radio" name="origem_tipo" value="fornecedor"
              checked={form.origem_tipo === 'fornecedor'} onChange={set('origem_tipo')} disabled={loading} />
            <span><strong>Fornecedor</strong> — NC contra um material/serviço que você comprou</span>
          </label>
        </div>
      </div>

      {/* Bloco do cliente emissor (quando origem = cliente) */}
      {form.origem_tipo === 'cliente' && (
        <div className="nc-form-group" style={{ marginBottom: 16, padding: '12px 16px', background: '#fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="nc-form-label">Cliente que reclamou * (aparece no relatório)</label>
            <input className="nc-form-input" type="text" value={form.cliente_emissor}
              onChange={set('cliente_emissor')} placeholder="Ex: COMERCIAL NORTE SUL LTDA" disabled={loading} />
          </div>
          <div>
            <label className="nc-form-label">Nº da NC no cliente</label>
            <input className="nc-form-input" type="text" value={form.numero_nc_cliente}
              onChange={set('numero_nc_cliente')} placeholder="Ex: 015/2026" disabled={loading} />
          </div>
          <div>
            <label className="nc-form-label">Prazo de resposta</label>
            <input className="nc-form-input" type="date" value={form.prazo_resposta_cliente}
              onChange={set('prazo_resposta_cliente')} disabled={loading} />
          </div>
          <div>
            <label className="nc-form-label">Email de contato</label>
            <input className="nc-form-input" type="email" value={form.contato_cliente_email}
              onChange={set('contato_cliente_email')} placeholder="qualidade@empresa-cliente.com.br" disabled={loading} />
          </div>
        </div>
      )}

      {/* Bloco do fornecedor (quando origem = fornecedor) */}
      {form.origem_tipo === 'fornecedor' && (
        <div className="nc-form-group" style={{ marginBottom: 16, padding: '12px 16px', background: '#fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: 6 }}>
          <label className="nc-form-label">Fornecedor envolvido *</label>
          <input className="nc-form-input" type="text" value={form.fornecedor_nome}
            onChange={set('fornecedor_nome')} placeholder="Ex: Metalúrgica Boa Vista Ltda — quem entregou o material não conforme" disabled={loading} />
          <small style={{ ...hintStyle, color: '#92400e' }}>
            A análise tratará a causa raiz como <strong>hipótese a confirmar com o fornecedor</strong> — você não tem
            acesso ao processo interno dele.
          </small>
        </div>
      )}

      {/* Cliente que detectou (só NC interna) */}
      {form.origem_tipo === 'proprio' && (
        <div className="nc-form-group">
          <label className="nc-form-label">Cliente que detectou (opcional)</label>
          <input className="nc-form-input" type="text" value={form.nome_cliente}
            onChange={set('nome_cliente')} placeholder="Ex: Indústria Aurora Ltda — quem reportou (se aplicável)" disabled={loading} />
        </div>
      )}

      {/* Detalhes — grid 2 colunas */}
      <div className="nc-form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="nc-form-label">Produto / Serviço</label>
          <input className="nc-form-input" type="text" value={form.produto}
            onChange={set('produto')} placeholder="Ex: Conexão hidráulica 1/2''" disabled={loading} />
        </div>

        {!ehServico && (
          <div>
            <label className="nc-form-label">Lote</label>
            <input className="nc-form-input" type="text" value={form.lote}
              onChange={set('lote')} placeholder="Ex: 2026-03-A" disabled={loading} />
          </div>
        )}

        <div>
          <label className="nc-form-label">Data da ocorrência</label>
          <input className="nc-form-input" type="date" value={form.data_ocorrencia}
            onChange={set('data_ocorrencia')} disabled={loading} />
        </div>

        <div>
          <label className="nc-form-label">Como foi detectada</label>
          <select className="nc-form-input" value={form.origem} onChange={set('origem')} disabled={loading}>
            <option value="">Selecione…</option>
            {ORIGEM_OPCOES.map((o) => <option key={o} value={o}>{o}</option>)}
            <option value={OUTRO_SENTINEL}>Outro…</option>
          </select>
          {form.origem === OUTRO_SENTINEL && (
            <input className="nc-form-input" type="text" value={origemCustom}
              onChange={(e) => setOrigemCustom(e.target.value)} placeholder="Descreva como foi detectada"
              style={{ marginTop: 8 }} maxLength={100} disabled={loading} />
          )}
        </div>

        <div>
          <label className="nc-form-label">Quantidade afetada</label>
          <input className="nc-form-input" type="text" value={form.quantidade_afetada}
            onChange={set('quantidade_afetada')} placeholder="Ex: 50 peças, 2 lotes, 100kg" disabled={loading} />
        </div>

        {!ehServico && (
          <div>
            <label className="nc-form-label">Status do lote</label>
            <select className="nc-form-input" value={form.status_lote} onChange={set('status_lote')} disabled={loading}>
              <option value="">Selecione…</option>
              <option value="Bloqueado">Bloqueado</option>
              <option value="Liberado">Liberado</option>
              <option value="Em análise">Em análise</option>
              <option value="Segregado">Segregado</option>
              <option value="Retrabalhado">Retrabalhado</option>
              <option value="Sucateado">Sucateado</option>
            </select>
          </div>
        )}

        {form.origem_tipo !== 'fornecedor' && (
          <div>
            <label className="nc-form-label">Indústria / setor</label>
            <select className="nc-form-input" value={form.industria} onChange={set('industria')} disabled={loading}>
              <option value="">Selecione a indústria…</option>
              {industrias.map((ind) => <option key={ind.codigo} value={ind.codigo}>{ind.nome}</option>)}
              <option value={OUTRO_SENTINEL}>Outro…</option>
            </select>
            {form.industria === OUTRO_SENTINEL && (
              <input className="nc-form-input" type="text" value={industriaCustom}
                onChange={(e) => setIndustriaCustom(e.target.value)} placeholder="Descreva o setor da sua empresa"
                style={{ marginTop: 8 }} maxLength={100} disabled={loading} />
            )}
            <small style={hintStyle}>O setor da <strong>sua empresa</strong> — orienta a IA a buscar a causa no seu processo.</small>
          </div>
        )}
      </div>

      {/* Situação atual — coaching REFORÇADO (Francis 15/06): o campo que mais pesa
          na qualidade da análise. Concreto p/ leigo (máquinas×pessoas, instrução
          documentada e se é seguida, treinamento) + a IA analisa SÓ com o que for
          informado. Escondido no outbound (o processo é do fornecedor). */}
      {form.origem_tipo !== 'fornecedor' && (
        <div className="nc-form-group">
          <label className="nc-form-label">Situação atual do processo</label>
          <small style={{ ...hintStyle, marginTop: 0, marginBottom: 8 }}>
            Conte como o seu processo funciona e o que pode ter influenciado o problema. É o campo que mais melhora a análise — você revisa o resultado depois.
          </small>
          {/* Coaching ACIMA do campo (Francis 15/06): a orientação tem que vir
              antes de escrever — sobretudo o aviso âmbar (mais impositivo). */}
          <div style={{
            marginBottom: 8, padding: '10px 12px', background: '#f0f7ff',
            borderLeft: '3px solid #4a90e2', borderRadius: 3,
            fontSize: 12, color: '#2c5282', lineHeight: 1.6,
          }}>
            <strong>O que ajuda a IA a achar a causa — descreva o que souber:</strong>
            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
              <li>Como o processo funciona, na prática (do começo ao fim do que deu errado)</li>
              <li>Se a etapa é feita por máquinas/equipamentos ou à mão, por uma pessoa (ou os dois)</li>
              <li>Se existe uma instrução de trabalho / procedimento escrito para essa etapa — ou se ela é feita "de cabeça"</li>
              <li>Se essa instrução é seguida no dia a dia — ou se costuma ter atalho/improviso</li>
              <li>Se quem faz é treinado e/ou experiente (ex: há quanto tempo na função, recebeu treinamento)</li>
              <li>Tudo o que pode ter influenciado: troca de turno, pressa, equipamento parado, material diferente, mudança recente no processo</li>
            </ul>
          </div>
          <div style={{
            marginBottom: 10, padding: '10px 12px', background: '#fffbeb',
            borderLeft: '3px solid #f59e0b', borderRadius: 3,
            fontSize: 12, color: '#92400e', lineHeight: 1.6,
          }}>
            A IA analisa <strong>apenas com as informações que você escrever aqui</strong> — ela não conhece a sua
            empresa nem visita o seu processo. Por isso, quanto mais detalhe você der, melhor e mais certeira fica a
            análise. E se você já desconfia da causa, diga aqui — a IA usa essa pista, sem ficar presa a ela.
          </div>
          <textarea
            className="nc-form-input" name="contexto_processo" rows="5"
            value={form.contexto_processo} onChange={set('contexto_processo')}
            placeholder="Ex: a peça é cortada numa máquina semiautomática; existe instrução de trabalho impressa no posto, mas nem sempre é seguida na correria; o operador entrou há 1 mês. Conte o seu caso assim — com o máximo de detalhe que puder."
            disabled={loading}
          />
        </div>
      )}

      {/* Aplicação no cliente / no seu processo (label muda no outbound) */}
      <div className="nc-form-group">
        <label className="nc-form-label">
          {form.origem_tipo === 'fornecedor'
            ? 'Aplicação do material no seu processo (opcional)'
            : 'Contexto da aplicação no cliente (opcional)'}
        </label>
        <textarea
          className="nc-form-input" name="contexto_setorial_custom" rows="2"
          value={form.contexto_setorial_custom} onChange={set('contexto_setorial_custom')}
          placeholder={form.origem_tipo === 'fornecedor'
            ? 'Como o material recebido é usado internamente e o risco em caso de falha. Ex: vigotas de laje em obra residencial, exigência NBR 6118, fissuras comprometem a carga.'
            : 'Como o seu produto é usado pelo cliente e qual o risco da NC para ele. Ex: vedação de compressor de refrigerador, exige resistência a -20°C, norma ASTM D2000.'}
          disabled={loading}
        />
        <small style={hintStyle}>
          Criticidade, normas exigidas, riscos. Ajuda a IA a dimensionar o impacto e priorizar as ações.
        </small>
      </div>

      {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}

      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" disabled={loading}>
          {loading ? 'SALVANDO...' : 'CONTINUAR PARA O PAGAMENTO'}
          <span className="material-symbols-outlined" style={{ marginLeft: '0.5rem' }}>arrow_forward</span>
        </Button>
      </div>
      <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 12 }}>
        Preencher é grátis. Você só paga (R$75) na próxima etapa, pra gerar a análise.
      </p>
    </form>
  );
}

export default AvulsaFillForm;
